package com.devsusana.portfolio.components.sections

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.widgets.TechBadge
import com.devsusana.portfolio.i18n.rememberStrings
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P

/**
 * Section highlighting Susana's learning journey and practical architecture in Agentic Programming.
 */
@Composable
fun AgenticJourneySection(modifier: Modifier = Modifier) {
    val colorMode = ColorMode.current
    val strings = rememberStrings()

    Column(
        modifier = modifier
            .fillMaxWidth()
            .id("agentic")
            .styleModifier { property("scroll-margin-top", "5rem") }
            .padding(topBottom = 3.cssRem),
        verticalArrangement = Arrangement.spacedBy(1.5.cssRem)
    ) {
        // Section Header
        Column(verticalArrangement = Arrangement.spacedBy(0.5.cssRem)) {
            SpanText(
                text = strings.agenticEyebrow,
                modifier = Modifier
                    .color(SitePalette.accentPurple)
                    .fontSize(0.9.cssRem)
                    .fontWeight(FontWeight.Bold)
            )

            H2 {
                SpanText(
                    text = strings.agenticTitle,
                    modifier = Modifier
                        .color(SitePalette.textPrimary(colorMode))
                        .styleModifier { property("font-size", "clamp(1.4rem, 4.5vw, 2rem)") }
                        .fontWeight(FontWeight.Bold)
                )
            }

            P {
                SpanText(
                    text = strings.agenticSubtitle,
                    modifier = Modifier
                        .color(SitePalette.textSecondary(colorMode))
                        .fontSize(1.1.cssRem)
                )
            }
        }

        // Three Core Pillars of Agentic Engineering (responsive grid: stacked on mobile, 3 columns on desktop)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 280px), 1fr))")
                    property("gap", "1rem")
                }
        ) {
            AgenticPillarCard(
                title = strings.agenticPillar1Title,
                description = strings.agenticPillar1Desc,
                colorMode = colorMode,
                modifier = Modifier.fillMaxWidth()
            )

            AgenticPillarCard(
                title = strings.agenticPillar2Title,
                description = strings.agenticPillar2Desc,
                colorMode = colorMode,
                modifier = Modifier.fillMaxWidth()
            )

            AgenticPillarCard(
                title = strings.agenticPillar3Title,
                description = strings.agenticPillar3Desc,
                colorMode = colorMode,
                modifier = Modifier.fillMaxWidth()
            )
        }

        // Practical Takeaways Box with fluid padding
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundColor(SitePalette.surface(colorMode))
                .borderRadius(16.px)
                .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                .styleModifier {
                    property("padding", "clamp(1.2rem, 3vw, 2rem)")
                }
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(1.cssRem)) {
                H3 {
                    SpanText(
                        text = strings.agenticPhilosophyTitle,
                        modifier = Modifier
                            .color(SitePalette.textPrimary(colorMode))
                            .fontSize(1.25.cssRem)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                SpanText(
                    text = strings.agenticPhilosophyText,
                    modifier = Modifier
                        .color(SitePalette.textSecondary(colorMode))
                        .lineHeight(1.6.cssRem)
                )

                Row(
                    horizontalArrangement = Arrangement.spacedBy(0.5.cssRem),
                    modifier = Modifier
                        .padding(top = 0.5.cssRem)
                        .styleModifier {
                            property("flex-wrap", "wrap")
                            property("gap", "0.5rem")
                        }
                ) {
                    TechBadge("Gemini 2.0 / 3.0", highlight = true)
                    TechBadge("Prompt Engineering")
                    TechBadge("Automated CI Audits")
                    TechBadge("Self-Healing Code")
                }

                Link(
                    path = "#blog",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = Modifier.padding(top = 0.5.cssRem)
                ) {
                    SpanText(
                        text = strings.agenticBlogLink,
                        modifier = Modifier
                            .color(SitePalette.accentPurple)
                            .fontWeight(FontWeight.Bold)
                    )
                }
            }
        }
    }
}

@Composable
private fun AgenticPillarCard(
    title: String,
    description: String,
    colorMode: ColorMode,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .backgroundColor(SitePalette.surface(colorMode))
            .borderRadius(12.px)
            .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
            .padding(1.25.cssRem)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(0.75.cssRem)) {
            SpanText(
                text = title,
                modifier = Modifier
                    .color(SitePalette.textPrimary(colorMode))
                    .fontWeight(FontWeight.Bold)
                    .fontSize(1.05.cssRem)
            )

            SpanText(
                text = description,
                modifier = Modifier
                    .color(SitePalette.textSecondary(colorMode))
                    .fontSize(0.9.cssRem)
                    .lineHeight(1.45.cssRem)
            )
        }
    }
}
